import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import ReceiptIcon from "@mui/icons-material/Receipt";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  IconButton,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { format } from "date-fns";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  deleteHolding,
  getBrokers,
  getHoldings,
} from "../../services/portfolioService";
import { useCurrency } from "../Auth/AuthContext";

const HoldingsPage = () => {
  const [holdings, setHoldings] = useState([]);
  const [brokers, setBrokers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterBroker, setFilterBroker] = useState("");
  const [filterType, setFilterType] = useState("");
  const [searchTicker, setSearchTicker] = useState("");
  const navigate = useNavigate();
  const {
    selectedCurrency,
    convert,
    formatMoney,
    ratesReady,
    userCurrency,
    convertAndFormat,
  } = useCurrency();

  useEffect(() => {
    fetchData();
  }, [filterBroker]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [holdingsData, brokersData] = await Promise.all([
        getHoldings(filterBroker || null),
        getBrokers(),
      ]);
      setHoldings(holdingsData);
      setBrokers(brokersData);
    } catch (error) {
      console.error("Error fetching data:", error);
      setError("Failed to load transactions");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this holding?")) {
      return;
    }

    try {
      await deleteHolding(id);
      await fetchData();
    } catch (error) {
      console.error("Error deleting holding:", error);
      setError(error.response?.data?.message || "Failed to delete holding");
    }
  };

  const formatCurrency = (value, currency = "USD") => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency,
    }).format(value);
  };

  const formatQuantity = (quantity) => {
    return parseFloat(quantity)
      .toFixed(4)
      .replace(/\.?0+$/, "");
  };

  const filteredHoldings = holdings.filter((holding) => {
    const matchesTicker =
      searchTicker === "" ||
      holding.ticker_symbol.toLowerCase().includes(searchTicker.toLowerCase());
    const matchesType =
      filterType === "" || holding.transaction_type === filterType;
    return matchesTicker && matchesType;
  });

  if (loading) {
    return (
      <Container
        maxWidth="lg"
        sx={{ mt: 4, display: "flex", justifyContent: "center" }}
      >
        <CircularProgress />
      </Container>
    );
  }

  const getTransactionIcon = (type) => {
    switch (type) {
      case "BUY":
        return "🟢";
      case "SELL":
        return "🔴";
      case "DIVIDEND":
        return "💵";
      case "DIVIDEND_REINVEST":
        return "💰";
      default:
        return "";
    }
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 3 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={3}
        >
          <Typography variant="h5" fontWeight={600}>
            <ReceiptIcon sx={{ mr: 1, verticalAlign: "middle" }} />
            Transaction History
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate("/add-stock-transaction")}
          >
            Add Transaction
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Filters */}
        <Box display="flex" gap={2} mb={3} flexWrap="wrap">
          <TextField
            select
            label="Transaction Type"
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            sx={{ minWidth: 180 }}
            size="small"
          >
            <MenuItem value="">All Types</MenuItem>
            <MenuItem value="BUY">🟢 BUY</MenuItem>
            <MenuItem value="SELL">🔴 SELL</MenuItem>
            <MenuItem value="DIVIDEND">💵 DIVIDEND</MenuItem>
            <MenuItem value="DIVIDEND_REINVEST">💰 DIVIDEND REINVEST</MenuItem>
          </TextField>

          <TextField
            select
            label="Filter by Broker"
            value={filterBroker}
            onChange={(e) => setFilterBroker(e.target.value)}
            sx={{ minWidth: 180 }}
            size="small"
          >
            <MenuItem value="">All Brokers</MenuItem>
            {brokers.map((broker) => (
              <MenuItem key={broker.id} value={broker.id}>
                {broker.name}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label="Search Ticker"
            value={searchTicker}
            onChange={(e) => setSearchTicker(e.target.value)}
            placeholder="e.g., AAPL"
            size="small"
            sx={{ minWidth: 180 }}
          />
        </Box>

        {filteredHoldings.length === 0 ? (
          <Box textAlign="center" py={6}>
            <ReceiptIcon
              sx={{ fontSize: 64, color: "text.secondary", mb: 2 }}
            />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No transactions yet
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={3}>
              Add your first transaction to start tracking your portfolio
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate("/add-stock-transaction")}
            >
              Add First Transaction
            </Button>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <strong>Date</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Type</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Ticker</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Company</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Broker</strong>
                  </TableCell>
                  <TableCell align="right">
                    <strong>Quantity</strong>
                  </TableCell>
                  <TableCell align="right">
                    <strong>Price</strong>
                  </TableCell>
                  <TableCell align="right">
                    <strong>Total</strong>
                  </TableCell>
                  <TableCell align="center">
                    <strong>Actions</strong>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredHoldings.map((holding) => (
                  <TableRow key={holding.id} hover>
                    <TableCell>
                      {format(new Date(holding.purchase_date), "MMM dd, yyyy")}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={`${getTransactionIcon(
                          holding.transaction_type,
                        )} ${holding.transaction_type}`}
                        size="small"
                        color={
                          holding.transaction_type === "BUY"
                            ? "success"
                            : holding.transaction_type === "SELL"
                              ? "error"
                              : "default"
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {holding.ticker_symbol}
                      </Typography>
                      {holding.exchange && (
                        <Typography variant="caption" color="text.secondary">
                          {holding.exchange}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{holding.company_name || "-"}</TableCell>
                    <TableCell>{holding.broker_name}</TableCell>
                    <TableCell align="right">
                      {holding.transaction_type === "DIVIDEND"
                        ? "-"
                        : formatQuantity(holding.quantity)}
                    </TableCell>
                    <TableCell align="right">
                      {convertAndFormat(
                        holding.purchase_price,
                        holding.currency,
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight={600}>
                        {convertAndFormat(holding.total_cost, holding.currency)}
                      </Typography>
                      {holding.fees > 0 && (
                        <Typography variant="caption" color="text.secondary">
                          (fees:{" "}
                          {convertAndFormat(holding.fees, holding.currency)})
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={() =>
                          navigate(`/edit-stock-transaction/${holding.id}`)
                        }
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDelete(holding.id)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {filteredHoldings.length > 0 && (
          <Box
            mt={2}
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="body2" color="text.secondary">
              Showing {filteredHoldings.length} transaction
              {filteredHoldings.length !== 1 ? "s" : ""}
            </Typography>
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default HoldingsPage;
